// @flow

import React from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { Link, ExpandableOverflow, MediaQuery } from '@riseart/common';
import { FilterService } from '@riseart/filter-service';
import { artist as ENUM_ARTIST, filter as ENUM_FILTER } from 'Enum';
import { Query } from 'shared_services/apollo/Query';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { FilterLinks } from 'shared_components/common/filters/links/Links';
import FILTER_ARTISTS_QUERY from 'shared_data/queries/filter/artists.graphql';

import {
  raScreenMd,
  raScreenMdMax,
  raScreenLg,
} from '@riseart/antd-provider/dist/website/variables.less';

type Props = {
  domain: string,
  medium?: string,
  subMedium?: string,
  style?: string,
  subject?: string,
  subSubject?: string,
  ignoreArtistIds?: string,
  sort?: string,
};

/**
 * FilterArtistsLinks
 */
export function FilterArtistLinks({
  domain,
  medium,
  style,
  ignoreArtistIds,
  sort = ENUM_ARTIST.rank.RANKING_FEATURED,
}: Props): Node {
  const { formatMessage } = useIntl();
  const filters = { medium, style };
  const mappedFilters =
    domain !== ENUM_FILTER.domain.ARTIST
      ? FilterService.mapFilters(filters, domain, ENUM_FILTER.domain.ARTIST)
      : filters;

  return (
    <MediaQuery minWidth={raScreenLg}>
      {(isDesktop) => (
        <MediaQuery minWidth={raScreenMd} maxWidth={raScreenMdMax}>
          {(isTablet) => (
            <Query
              ssr={false}
              query={FILTER_ARTISTS_QUERY}
              variables={{
                inputArtistFilter: {
                  items: isTablet ? 15 : 16,
                  page: 1,
                  sort,
                  ...(mappedFilters.medium ? { medium: mappedFilters.medium } : {}),
                  ...(mappedFilters.style ? { style: mappedFilters.style } : {}),
                },
                ...(ignoreArtistIds ? { ignoreArtistIds } : null),
              }}
            >
              {({ data }) => {
                const { filterArtists: { items } = {} } = data || {};

                if (!items) {
                  return <IsomorphicRipple isActive />;
                }

                const linksComponent = (
                  <FilterLinks
                    maxColumns={isTablet ? 3 : 4}
                    items={items.map((item) => {
                      const name = formatMessage({ id: 'common.artistName' }, { name: item.name });

                      return (
                        <Link
                          type="light"
                          to={UrlAssembler.artistProfile(item.id, item.alias)}
                          title={name}
                        >
                          {name}
                        </Link>
                      );
                    })}
                  />
                );

                return isTablet || isDesktop ? (
                  linksComponent
                ) : (
                  <ExpandableOverflow
                    maxHeight={160}
                    showMore={formatMessage({ id: 'common.show_more' })}
                    showLess={formatMessage({ id: 'common.show_less' })}
                  >
                    {linksComponent}
                  </ExpandableOverflow>
                );
              }}
            </Query>
          )}
        </MediaQuery>
      )}
    </MediaQuery>
  );
}
